/** Derivarea 3D: din planșeele desenate, o scenă de extrudat.
 *
 *  UN SINGUR STRAT DE DERIVARE, aici, în TypeScript — nu al doilea traducător de
 *  documente. Ce scoate seamănă cu ce desenează `StoreyView` în SVG, fiindcă e
 *  ACEEAȘI lectură a documentului (`StoreyPlan`): camerele cu inelele lor,
 *  pereții, placa. Ce adaugă e a treia dimensiune — o cotă pentru fiecare lucru
 *  — și un singur artificiu, tăierea.
 *
 *  PEREȚII SE DERIVĂ DIN INELELE CAMERELOR, nu din obiectele `Wall`. Nu e o
 *  scurtătură: documentele solverului n-au NICIUN `Wall` (verificat pe
 *  `testt<3` — 3.302 `Space`, 480 `Door`, zero `Wall`), fiindcă etapa care le-ar
 *  emite e importul DWG. Peretele dintre două camere e, în documentele astea,
 *  chiar spațiul dintre inelele lor: fiecare inel se strânge cu jumătate de
 *  grosime, iar muchia lui devine o față de perete. Un randor care ar cere
 *  `Wall` ar desena, pe planurile generate, camere fără pereți.
 *
 *  TĂIEREA LA 1,15 m SE MODELEAZĂ, nu se face cu `clippingPlanes`: three taie
 *  fără să capaceze, iar un perete tăiat și necapacat se vede GOL exact din
 *  vederea de sus, care e cea cerută. Deci pereții se construiesc direct la
 *  înălțimea de tăiere, ca o secțiune orizontală adevărată.
 */
import type { CanonicalDoc, StoreyPlan, StoreyShape } from "../canonical";

export type Pt = [number, number];

/** Cota la care se taie macheta. Un plan de arhitectură e o secțiune orizontală
 *  la circa 1,20 m — deasupra pervazurilor, sub buiandrugi — și asta e cota la
 *  care se citește un plan. */
export const TAIERE = 1.15;
/** Jumătatea de grosime cu care se strânge fiecare inel. Două camere alăturate
 *  se strâng fiecare cu atât, deci peretele dintre ele iese de 2 × 0,09. */
export const GROSIME = 0.09;
/** Cât de aproape de o muchie trebuie să cadă un gol ca să fie AL EI. */
const LIPIRE = 0.28;

export interface Camera3 {
  /** Identitatea din document — același `id` de `Space` pe care îl poartă
   *  `ApartmentRoom.eid` și la care se referă raportul validatorului. Prin ea
   *  se leagă un click din machetă de selecția pe care o citește panoul, deci
   *  2D și 3D ajung la ACELEAȘI informații fără două căi de rezolvare. */
  eid: string;
  kind: string;
  eticheta: string;
  arie: number;
  /** Centrul vârfurilor, pentru etichetă. */
  c: Pt;
  /** Latura mică a cadrului — sub un prag, eticheta nu încape. */
  mic: number;
  inel: Pt[];
}

/** Un tronson de perete: două capete și înălțimea la care e tăiat. Parapetul
 *  unei ferestre e tot un tronson, doar mai scund. */
/** Un tronson de perete: axa lui, înălțimea și grosimea — ale peretelui din
 *  soluție, tăiat la goluri. */
export interface Perete3 { a: Pt; b: Pt; h: number; t: number }

/** Un loc de parcare, cu mașina lui — dacă e ocupat. */
export interface Loc3 {
  c: Pt; deg: number; w: number; h: number; ada: boolean;
  /** Numărul vopsit pe pardoseală, din `ParkingSpace.display.n`. */
  n: string;
  masina: Masina3 | null;
}
export interface Masina3 { m: string; cul: number }
/** Un dreptunghi așezat pe placă: stâlp sau culoar PMR. */
export interface Cutie3 { c: Pt; deg: number; w: number; h: number }

/** Ce stă pe un planșeu de parcaj. `null` pe un nivel care nu e parcaj. */
export interface Parcaj3 {
  locuri: Loc3[];
  stalpi: Cutie3[];
  culoare: Cutie3[];
  benzi: [Pt, Pt][];
  rampe: Pt[][];
}

/** Culorile mașinilor — cinci tonuri stinse și două calde, niciunul saturat.
 *  Un subsol cu mașini tari devine un covor de confetti și acoperă tocmai ce
 *  trebuie citit: marcajul. */
export const CULORI_MASINI = ["#c8c3b8", "#8f9a93", "#a8705a", "#6f7885",
                              "#d7d2c6", "#4f5450", "#b9a68c"];
/** Ponderat: berlina și SUV-ul de câte trei ori, duba o dată. Un parcaj de bloc
 *  cu tot atâtea dube câte berline arată a depozit. */
const MODELE_MASINI = ["sedan", "suv", "hatchback-sports", "sedan", "suv-luxury",
                       "sedan-sports", "suv", "van", "hatchback-sports", "delivery"];
/** Înălțimea stâlpului, de la placă la grindă. */
export const STALP_H = 2.8;

/** Centrul, gabaritul și unghiul unui dreptunghi dat ca inel.
 *
 *  Locurile și stâlpii se scriu în document prin centru + `w/h/deg`, dar
 *  `canonical.bara` le-a transformat deja în inel ca să existe o singură formă
 *  de citit. Aici se face drumul înapoi, din inel — și tocmai de aceea merge la
 *  fel pe locurile de la sol, care vin cu inel propriu și n-au avut niciodată
 *  `w/h`. Prima muchie dă direcția: pentru un dreptunghi construit din centru,
 *  ea e chiar latura `w`. */
export function dreptunghi(inel: readonly Pt[]): Cutie3 | null {
  // `curata` ÎNTÂI, și nu de formă: `canonical.inelDinCentru` întoarce inelul
  // ÎNCHIS — `[a, b, d, e, a]`, cu primul colț repetat — iar o medie peste toate
  // cele cinci puncte îl numără pe `a` de două ori și trage centrul spre el cu o
  // cincime din semi-diagonală. Pe un loc de 2,50 × 5,50 asta înseamnă 0,60 m:
  // marcajul și mașina ieșeau din locul lor, decalate față de alei și de stâlpi
  // (care alunecau și ei, dar cu alt vector, având alt gabarit). Laturile și
  // unghiul erau corecte, fiindcă se citesc din primele trei vârfuri — de-aia un
  // test care verifică numai `w` și `h` nu prinde nimic.
  const P = curata(inel);
  if (P.length < 4) return null;
  const [p0, p1, p2] = P;
  const w = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
  const h = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
  if (!(w > 0) || !(h > 0)) return null;
  const n = P.length;
  const c: Pt = [P.reduce((a, q) => a + q[0], 0) / n,
                 P.reduce((a, q) => a + q[1], 0) / n];
  return { c, w, h, deg: (Math.atan2(p1[1] - p0[1], p1[0] - p0[0]) * 180) / Math.PI };
}

export interface Corp3 {
  cod: string;
  /** Direcția barei, în grade. Pe ea se aliniază ecranul în vederile de sus:
   *  un ansamblu are bare pe direcții diferite, iar desenat pe nord oricare
   *  iese pe diagonală și pare strâmb trasat, deși nu e. */
  deg: number;
  placa: Pt[][];
  camere: Camera3[];
  pereti: Perete3[];
  parcaj: Parcaj3 | null;
}

export interface Scena3 {
  taiere: number;
  grosime: number;
  corpuri: Corp3[];
}

// ---------------------------------------------------------------- geometrie

/** Camera din inelul căreia cade punctul, sau `null` între ele.
 *
 *  Aruncare de rază pe orizontală, cu numărare de traversări — algoritmul
 *  obișnuit. E de ajuns: inelele camerelor nu se suprapun, iar un nivel are
 *  ordinul sutelor, deci căutarea liniară costă mai puțin decât indexul care
 *  ar evita-o.
 *
 *  Inelele sunt cele STRÂNSE cu `ingust`, adică pardoseala desenată. Un click
 *  pe peretele dintre două camere cade deci între ele și nu alege niciuna —
 *  ceea ce e răspunsul onest: peretele nu e nici a uneia, nici a alteia.
 */
export function cameraDinPunct(camere: readonly Camera3[],
                               x: number, y: number): Camera3 | null {
  for (const c of camere) {
    const n = c.inel.length;
    let inauntru = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const [xi, yi] = c.inel[i];
      const [xj, yj] = c.inel[j];
      if ((yi > y) !== (yj > y)
          && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inauntru = !inauntru;
      }
    }
    if (inauntru) return c;
  }
  return null;
}

/** Inelul fără puncte duplicate și fără capătul care repetă începutul. */
function curata(poly: readonly number[][], eps = 1e-7): Pt[] {
  const out: Pt[] = [];
  for (const p of poly) {
    const u = out[out.length - 1];
    if (!u || Math.abs(p[0] - u[0]) > eps || Math.abs(p[1] - u[1]) > eps) {
      out.push([p[0], p[1]]);
    }
  }
  if (out.length > 1) {
    const a = out[0], b = out[out.length - 1];
    if (Math.abs(a[0] - b[0]) < eps && Math.abs(a[1] - b[1]) < eps) out.pop();
  }
  return out;
}

/** Aria orientată — semnul ei spune sensul de parcurgere. */
function ariaInelului(poly: readonly Pt[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x0, y0] = poly[i];
    const [x1, y1] = poly[(i + 1) % poly.length];
    s += x0 * y1 - x1 * y0;
  }
  return s / 2;
}

/** Inelul strâns cu `d` spre interior.
 *
 *  Se mută fiecare LATURĂ paralel cu ea însăși și se intersectează vecinele —
 *  nu se mută vârfurile spre centru, care ar strica unghiurile. Pe colțuri
 *  aproape coliniare intersecția e instabilă, deci acolo se păstrează vârful. */
/** Cât se lasă vârful strâns să se depărteze de cel original, în multipli de
 *  grosime. Patru înseamnă că un vârf sub ~28° se teșește. */
const LIMITA_SANFREN = 4;

export function ingust(poly: readonly number[][], d: number): Pt[] {
  let P = curata(poly);
  if (P.length < 3) return P;
  if (ariaInelului(P) < 0) P = P.slice().reverse();
  // Latura mutată spre interior, ȘI vârful din care pleacă: pe el se măsoară
  // limita de șanfren de mai jos.
  const linii: [number, number, number, number, number, number][] = [];
  for (let i = 0; i < P.length; i++) {
    const [x0, y0] = P[i];
    const [x1, y1] = P[(i + 1) % P.length];
    const dx = x1 - x0, dy = y1 - y0;
    const L = Math.hypot(dx, dy);
    if (L < 1e-9) continue;
    const ux = dx / L, uy = dy / L;
    linii.push([x0 - uy * d, y0 + ux * d, ux, uy, x0, y0]);
  }
  const out: Pt[] = [];
  for (let i = 0; i < linii.length; i++) {
    const [ax, ay, adx, ady] = linii[(i - 1 + linii.length) % linii.length];
    const [bx, by, bdx, bdy, vx, vy] = linii[i];
    const den = adx * bdy - ady * bdx;
    if (Math.abs(den) < 1e-7) { out.push([bx, by]); continue; }
    const t = ((bx - ax) * bdy - (by - ay) * bdx) / den;
    const px = ax + adx * t, py = ay + ady * t;
    // LIMITĂ DE ȘANFREN — și nu e un detaliu de rafinament.
    //
    // Vârful inelului strâns e intersecția celor două laturi mutate, iar
    // distanța lui față de vârful original crește cu `d / tan(unghi/2)`. Pe un
    // inel de import, un vârf aproape întins e obișnuit: o cotitură de o
    // jumătate de grad aruncă punctul la vreo 20 m. Măsurat pe un document
    // real: un hol de 2,0 m rază primea un vârf strâns la 46,2 m — un ac
    // subțire care traversa tot desenul, plecând din clădire în câmp.
    //
    // Garda de pe `den` nu ajută: `den` e sinusul unghiului, iar 1e-7 prinde
    // doar vârfurile sub șase microradiani. Peste limită se renunță la
    // intersecție și se ia capătul mutat al laturii — un colț teșit în loc de
    // o săgeată. Teșitura e invizibilă la 0,09 m; săgeata nu era.
    if (Math.hypot(px - vx, py - vy) > LIMITA_SANFREN * d) out.push([bx, by]);
    else out.push([px, py]);
  }
  return out;
}

// ---------------------------------------------------------------- golurile

/** Un gol, ca segment pe fața peretelui, cu parapetul lui.
 *  `parapet = 0` — ușă, deschisă până la pardoseală. */
export interface Gol3 { a: Pt; b: Pt; parapet: number }

/** Segmentul lung al unui gol desenat ca inel: cea mai lungă diagonală dintre
 *  vârfurile lui. Un gol e un dreptunghi subțire, deci diagonala lungă e chiar
 *  axa lui — și se obține fără să știm în ce ordine sunt scrise vârfurile. */
export function segmentulGolului(o: StoreyShape): Gol3 | null {
  if (o.cx != null && o.cy != null && o.width_m && o.deg != null) {
    const a = (o.deg * Math.PI) / 180, hw = o.width_m / 2;
    const ux = Math.cos(a), uy = Math.sin(a);
    return { a: [o.cx - ux * hw, o.cy - uy * hw],
             b: [o.cx + ux * hw, o.cy + uy * hw], parapet: 0 };
  }
  const P = curata(o.poly ?? []);
  if (P.length < 2) return null;
  let best = -1, ia = 0, ib = 1;
  for (let i = 0; i < P.length; i++) {
    for (let j = i + 1; j < P.length; j++) {
      const d = Math.hypot(P[j][0] - P[i][0], P[j][1] - P[i][1]);
      if (d > best) { best = d; ia = i; ib = j; }
    }
  }
  return { a: P[ia], b: P[ib], parapet: 0 };
}

/** Golurile nivelului, ca segmente.
 *
 *  DOUĂ SURSE, în ordinea asta. Întâi cele pe care documentul le așază singur —
 *  `StoreyPlan.openings`, adică inelele pe care `canonical.golul` a putut să le
 *  rezolve. Apoi, pentru documentele solverului, `display.jambs`.
 *
 *  A doua e o CONCESIE, și se scrie ca atare. `canonical.golul` refuză
 *  dinadins să citească `display` — schema spune că un cititor are voie să-l
 *  ignore, deci o ușă care își ține poziția acolo n-are, formal, unde să stea;
 *  planul 2D o numără în `fara_loc` și n-o desenează. Corect ca principiu, dar
 *  aici costul e altul: pe documentele generate TOATE ușile sunt acolo (480 din
 *  480 pe `testt<3`), iar fără ele fiecare cameră iese o cutie închisă, fără
 *  nicio ușă, ceea ce nu e o reprezentare mai onestă — e una vizibil falsă.
 *  Când etapa de arhitectură va scrie golurile în `geometry`, ramura asta pică
 *  singură: prima sursă le va conține deja.
 *
 *  `display` NU e deplasat de `laOrigineLocala` (deplasează doar `geometry`),
 *  deci jamburile vin în coordonate absolute și se mută aici. Fără asta, ușile
 *  ar cădea la sute de kilometri de camerele lor. */
function goluriNivel(plans: readonly StoreyPlan[], doc: CanonicalDoc | null,
                     origine: [number, number]): Gol3[] {
  const out: Gol3[] = [];
  for (const p of plans) {
    for (const o of p.openings) {
      const s = segmentulGolului(o);
      if (s) out.push({ ...s, parapet: o.tip === "Window" ? 0.85 : 0 });
    }
  }
  const idPlanșee = new Set(plans.map((p) => p.id));
  const [ox, oy] = origine;
  for (const o of doc?.objects ?? []) {
    if (o.type !== "Door" && o.type !== "Window") continue;
    if (!idPlanșee.has((o.properties.storey ?? "") as string)) continue;
    // Numai cele pe care prima sursă NU le-a putut așeza.
    const geo = o.geometry ?? {};
    if (geo.poly || (geo.cx != null && geo.cy != null) || geo.u_m != null) continue;
    const j = (o.display?.jambs ?? null) as number[][] | null;
    if (!j || j.length < 2) continue;
    out.push({
      a: [j[0][0] - ox, j[0][1] - oy],
      b: [j[1][0] - ox, j[1][1] - oy],
      parapet: o.type === "Window" ? ((geo.sill_m as number) ?? 0.85) : 0,
    });
  }
  return out;
}

// ---------------------------------------------------------------- pereții

/** Muchia unui inel, tăiată acolo unde cad goluri pe ea.
 *
 *  Un gol deschis până la pardoseală (ușa) lasă o ruptură; unul cu parapet
 *  (fereastra) lasă un tronson scund, cât parapetul — de acolo se vede glaful
 *  din vederea de sus, care altfel ar fi o gaură ca oricare alta. */
function taieMuchia(A: Pt, B: Pt, goluri: readonly Gol3[], out: Perete3[],
                    t = GROSIME * 2, sus = TAIERE): void {
  const dx = B[0] - A[0], dy = B[1] - A[1];
  const L = Math.hypot(dx, dy);
  if (L < 0.05) return;
  const ux = dx / L, uy = dy / L;
  const taieri: { t0: number; t1: number; parapet: number }[] = [];
  for (const g of goluri) {
    const ts: number[] = [];
    let ok = true;
    for (const pct of [g.a, g.b]) {
      const t = (pct[0] - A[0]) * ux + (pct[1] - A[1]) * uy;
      const dist = Math.abs(-(pct[0] - A[0]) * uy + (pct[1] - A[1]) * ux);
      if (dist > LIPIRE || t < -0.15 || t > L + 0.15) { ok = false; break; }
      ts.push(t);
    }
    if (!ok || ts.length < 2) continue;
    taieri.push({ t0: Math.max(0, Math.min(...ts)),
                  t1: Math.min(L, Math.max(...ts)), parapet: g.parapet });
  }
  taieri.sort((a, b) => a.t0 - b.t0);
  const pe = (t: number): Pt => [A[0] + ux * t, A[1] + uy * t];
  let poz = 0;
  for (const { t0, t1, parapet } of taieri) {
    if (t0 - poz > 0.02) out.push({ a: pe(poz), b: pe(t0), h: sus, t });
    if (parapet > 0.02) {
      out.push({ a: pe(t0), b: pe(t1), h: Math.min(parapet, sus), t });
    }
    poz = Math.max(poz, t1);
  }
  if (L - poz > 0.02) out.push({ a: pe(poz), b: pe(L), h: sus, t });
}

// ---------------------------------------------------------------- scena

const centru = (P: readonly Pt[]): Pt => {
  let x = 0, y = 0;
  for (const p of P) { x += p[0]; y += p[1]; }
  return [x / P.length, y / P.length];
};

const laturaMica = (P: readonly Pt[]): number => {
  const xs = P.map((p) => p[0]), ys = P.map((p) => p[1]);
  return Math.min(Math.max(...xs) - Math.min(...xs),
                  Math.max(...ys) - Math.min(...ys));
};

/** Direcția barei corpului: latura cea mai lungă a plăcii lui.
 *
 *  Pe ea se rotește ecranul în vederile de sus. Un ansamblu are bare pe direcții
 *  diferite (91°, 95°, 177° pe ansamblul de referință); desenat pe nord,
 *  oricare iese pe diagonală. */
function directia(placa: readonly Pt[][]): number {
  let best = -1, deg = 0;
  for (const inel of placa) {
    for (let i = 0; i < inel.length; i++) {
      const [x0, y0] = inel[i];
      const [x1, y1] = inel[(i + 1) % inel.length];
      const d = Math.hypot(x1 - x0, y1 - y0);
      if (d > best) { best = d; deg = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI; }
    }
  }
  return deg;
}

/** Scena celor desenate: un corp per planșeu, cu camerele, pereții și placa lui.
 *
 *  Un corp PER PLANȘEU, nu per clădire: la «toate corpurile» sunt mai multe
 *  planșee la aceeași cotă, iar fiecare are direcția lui — care e chiar ce
 *  decide cum se rotește ecranul deasupra lui. */
/** Ce stă pe un planșeu de parcaj, adus din plan în machetă.
 *
 *  OCUPAREA ȘI CULOAREA se hotărăsc AICI, o dată, după aceeași regulă ca planșa
 *  desenată: ocupat dacă `(i·7) mod 10 < 6`, culoarea `(i·3) mod 7`. Puse în
 *  randor, s-ar fi reașezat la fiecare recompunere a scenei și aceeași mașină ar
 *  fi sărit de pe un loc pe altul la simpla schimbare a camerei. Locurile PMR
 *  rămân goale: un loc rezervat, ocupat în desen, spune exact pe dos. */
function parcajul(p: StoreyPlan): Parcaj3 | null {
  if (!p.parcare && !p.bays.length) return null;
  const locuri: Loc3[] = [];
  p.bays.forEach((b, i) => {
    const d = dreptunghi(b.poly as Pt[]);
    if (!d) return;
    const ada = b.kind === "ada";
    locuri.push({
      ...d, ada, n: b.label ?? "",
      masina: !ada && (i * 7) % 10 < 6
        ? { m: MODELE_MASINI[(i * 13) % MODELE_MASINI.length], cul: (i * 3) % CULORI_MASINI.length }
        : null,
    });
  });
  const cutii = (l: readonly StoreyShape[]) =>
    l.map((x) => dreptunghi(x.poly as Pt[])).filter((x): x is Cutie3 => x !== null);
  const benzi: [Pt, Pt][] = [];
  for (const l of p.lanes) {
    const ax = l.axis as number[][] | undefined;
    if (ax && ax.length >= 2) benzi.push([[ax[0][0], ax[0][1]], [ax[1][0], ax[1][1]]]);
  }
  return {
    locuri,
    stalpi: cutii(p.columns),
    culoare: cutii(p.aisles),
    benzi,
    rampe: p.ramps.map((r) => curata(r.poly)).filter((r) => r.length >= 3),
  };
}

/** Axa și grosimea unui perete: cele scrise, altfel din dreptunghiul lui —
 *  latura lungă e axa, cea scurtă grosimea. */
function axaPeretelui(w: StoreyShape): { a: Pt; b: Pt; t: number } | null {
  const ax = w.axis;
  if (ax && ax.length >= 2) {
    const a = ax[0], b = ax[ax.length - 1];
    return { a: [a[0], a[1]], b: [b[0], b[1]], t: w.thickness_m ?? GROSIME * 2 };
  }
  const P = curata(w.poly ?? []);
  if (P.length !== 4) return null;
  const l = (i: number) => Math.hypot(P[(i + 1) % 4][0] - P[i][0], P[(i + 1) % 4][1] - P[i][1]);
  const i = l(0) >= l(1) ? 1 : 0;   // latura SCURTĂ de la care pleacă axa
  const m = (u: Pt, v: Pt): Pt => [(u[0] + v[0]) / 2, (u[1] + v[1]) / 2];
  return { a: m(P[i], P[(i + 1) % 4]), b: m(P[(i + 2) % 4], P[(i + 3) % 4]), t: l(i) };
}

export function scena3D(plans: readonly StoreyPlan[], doc: CanonicalDoc | null,
                        origine: [number, number] = [0, 0]): Scena3 {
  const goluri = goluriNivel(plans, doc, origine);
  const corpuri: Corp3[] = [];
  for (const p of plans) {
    const camere: Camera3[] = [];
    const pereti: Perete3[] = [];
    // MOBILA NU SE DEDUCE: soluția n-o are, deci macheta n-o arată (Stefan,
    // 23.09.2026: «dacă generatorul nu pune mobila atunci nu o mai pune
    // nicăieri»).
    // Spațiile ȘI părțile nucleului: pe un etaj generat, casa scării și
    // ascensoarele sunt `CorePart`, nu `Space`, și macheta le lăsa un gol în
    // mijlocul plăcii. Au pardoseală de beton și pereți ca orice cameră.
    for (const s of [...p.spaces, ...p.cores]) {
      if (!s.kind || (s.poly?.length ?? 0) < 3) continue;
      const inel = ingust(s.poly, GROSIME);
      if (inel.length < 3) continue;
      camere.push({
        eid: s.eid,
        kind: s.kind,
        eticheta: s.label ?? "",
        arie: s.area ?? 0,
        c: centru(inel),
        mic: laturaMica(inel),
        inel,
      });
    }
    // PEREȚII SUNT AI SOLUȚIEI (Stefan, 24.09.2026: «nu vrem niciodată să
    // desenăm ceva ce nu există în soluție»). Până acum fiecare latură de
    // cameră ridica un perete de 2 × 0,09, oricare ar fi fost pereții
    // documentului — iar un document fără pereți ieșea cu pereți. Acum se
    // ridică obiectele `Wall`, pe axa și cu grosimea lor, tăiate la goluri; un
    // nivel fără pereți iese fără pereți.
    for (const w of p.walls) {
      const ax = axaPeretelui(w);
      if (!ax) continue;
      const sus = typeof w.height_m === "number" && w.height_m > 0
        ? Math.min(w.height_m, TAIERE) : TAIERE;
      taieMuchia(ax.a, ax.b, goluri, pereti, ax.t, sus);
    }
    // Placa: planșeele desenate, iar când lipsesc — conturul planșeului.
    // …dar numai PLANȘEELE: o placă în consolă (`cantilever` — copertina de
    // peste o cutie de balcon, format 3.5) nu e planșeul nivelului, iar luată
    // drept el macheta rămânea cu o fâșie de placă în loc de etaj. Nici o
    // placă LA ALTĂ COTĂ (`offset`, 3.12: holul de intrare coborât la teren)
    // — e o zonă a planșeului, nu planșeul; luată drept el, etajul se
    // strângea la cei trei metri ai holului.
    const plansee = p.slabs.filter((sl) => sl.kind !== "cantilever" && sl.kind !== "offset"
      && (sl.poly?.length ?? 0) >= 3);
    const placa = plansee.length
      ? plansee.map((sl) => curata(sl.poly))
      : (p.parts?.length ? p.parts : (p.outline.length >= 3 ? [p.outline] : [])).map(curata);
    corpuri.push({
      cod: p.cod || p.label,
      deg: directia(placa.length ? placa : camere.map((c) => c.inel)),
      placa, camere, pereti,
      parcaj: parcajul(p),
    });
  }
  return { taiere: TAIERE, grosime: GROSIME * 2, corpuri };
}
